import type { FulfilmentStatus } from './orders';

/**
 * Write-offs (Phase 7B-1, ADR-031): a walk-out, a staff meal or the owner's comp waives what an order owes. A write-off
 * is never money received: no payment row, no method total, no cash expected in the drawer. The order settles as
 * written off once what was paid (net of refunds) and what is written off cover its total.
 *
 * The cashier writes off alone within the allowance the owner set (D1, D3); beyond it, and for every comp, the owner
 * approves through a single-use grant (D2, ADR-023). These helpers mirror `app.write_off_assess` so the counter can
 * say in advance what the owner will be asked; the server reads the day under its lock and decides.
 */

export const WRITE_OFF_KINDS = ['walkout', 'staff_meal', 'comp'] as const;
export type WriteOffKind = (typeof WRITE_OFF_KINDS)[number];
/** A ledger row: a write-off, or the reinstatement of one. */
export type WriteOffRowKind = WriteOffKind | 'reinstated';

export const WRITE_OFF_LABEL: Record<WriteOffRowKind, string> = {
  walkout: 'Walk-out',
  staff_meal: 'Staff meal',
  comp: 'Comp',
  reinstated: 'Reinstated',
};

export type WriteOffAuthority = 'cashier' | 'owner';

/** Why the owner must approve. Mirrors the `why` codes of `app.write_off_assess`. */
export const WRITE_OFF_WHY = [
  'over_order_max',
  'over_day_total',
  'over_day_count',
  'history',
  'over_meal_day',
  'comp',
] as const;
export type WriteOffWhy = (typeof WRITE_OFF_WHY)[number];

export const WRITE_OFF_WHY_LABEL: Record<WriteOffWhy, string> = {
  over_order_max: 'More than a cashier writes off or returns on one order',
  over_day_total: 'Over the day’s total of walk-outs and returns for this location',
  over_day_count: 'Over the day’s walk-outs and returns by cashiers',
  history: 'The order was refunded, voided, corrected or written off before',
  over_meal_day: 'Over this employee’s staff meals for the day',
  comp: 'A comp is the owner’s',
};

/** What on an order makes a walk-out the owner's (D2). Mirrors `app.order_write_off_history`. */
export const WRITE_OFF_HISTORY = [
  'refund',
  'payment_void',
  'line_void',
  'correction',
  'serve_correction',
  'write_off',
  'return',
] as const;
export type WriteOffHistory = (typeof WRITE_OFF_HISTORY)[number];

export const WRITE_OFF_HISTORY_LABEL: Record<WriteOffHistory, string> = {
  refund: 'money given back',
  payment_void: 'a payment voided',
  line_void: 'a line voided',
  correction: 'a correction of its money',
  serve_correction: 'a serve undone',
  write_off: 'an earlier write-off',
  return: 'an earlier return',
};

export const WRITE_OFF_REASON_MAX = 200;

/** The owner's limits (settings writeoff.*; owner-only). */
export interface WriteOffLimits {
  walkoutMaxMinor: number;
  dayMaxMinor: number;
  dayMaxCount: number;
  staffMealDayMinor: number;
}

/** D1 and D3 as approved: ₱500 an order, ₱1,000 and 2 walk-outs a location's day, ₱300 an employee's staff meals a day. */
export const DEFAULT_WRITE_OFF_LIMITS: WriteOffLimits = {
  walkoutMaxMinor: 50_000,
  dayMaxMinor: 100_000,
  dayMaxCount: 2,
  staffMealDayMinor: 30_000,
};

/** What is owed and not yet written off: the most a write-off can be (a walk-out is all of it). */
export function writeOffBalanceMinor(o: {
  total_minor: number;
  paid_minor: number;
  refunded_minor: number;
  written_off_minor?: number | null;
}): number {
  return Math.max(
    0,
    o.total_minor - (o.paid_minor - o.refunded_minor) - (o.written_off_minor ?? 0),
  );
}

/** A walk-out is what was served and not paid: food not yet served is voided, not written off. */
export function canWalkOut(fulfilment: FulfilmentStatus): boolean {
  return fulfilment === 'served' || fulfilment === 'not_required';
}

/**
 * Why a walk-out of `amountMinor` needs the owner, given the location's day so far: every walk-out counts toward the
 * total (whoever authorised it, reinstated or not); only the cashiers' count toward the number. Empty: the cashier
 * writes it off alone.
 */
export function walkoutNeedsOwner(input: {
  amountMinor: number;
  dayTotalMinor: number;
  dayCashierCount: number;
  history: readonly string[];
  limits: WriteOffLimits;
}): WriteOffWhy[] {
  const why: WriteOffWhy[] = [];
  if (input.amountMinor > input.limits.walkoutMaxMinor) why.push('over_order_max');
  if (input.dayTotalMinor + input.amountMinor > input.limits.dayMaxMinor)
    why.push('over_day_total');
  if (input.dayCashierCount + 1 > input.limits.dayMaxCount) why.push('over_day_count');
  if (input.history.length > 0) why.push('history');
  return why;
}

/** Why a staff meal needs the owner, given that employee's meals of the business day so far. */
export function staffMealNeedsOwner(input: {
  amountMinor: number;
  employeeDayMinor: number;
  limits: WriteOffLimits;
}): WriteOffWhy[] {
  return input.employeeDayMinor + input.amountMinor > input.limits.staffMealDayMinor
    ? ['over_meal_day']
    : [];
}

/** The reason a write-off carries: required, trimmed, at most 200 characters. */
export function writeOffReasonError(reason: string): string | null {
  const r = reason.trim();
  if (!r) return 'A reason is required';
  if (r.length > WRITE_OFF_REASON_MAX) return `At most ${WRITE_OFF_REASON_MAX} characters`;
  return null;
}
